import { FC, useState, createContext, useContext, useEffect } from 'react';
import { option } from 'fp-ts';
import { FieldType } from 'modules/domains';
import { useCatalogContext } from 'modules/catalog/catalogSelector';
import { noop } from 'utils/noop';
import { useCatalogQueryContext } from '../../../contexts/catalogQuery';
import { useCatInSegQueryBuilderV2FeatureFlag } from '../hooks/useCatInSegQueryBuilderV2FeatureFlag';

type CatalogSelection = {
  isMatchingOnKey: boolean;
  isMatchingOnProperty: boolean;
  hasCatalog: boolean;
  isEnabled: boolean;
  setIsEnabled: (val: boolean) => void;
  update: (option: boolean) => void;
};

export const defaultCatalogSelection = {
  isMatchingOnKey: false,
  isMatchingOnProperty: false,
  hasCatalog: false,
  isEnabled: false,
  setIsEnabled: noop,
  update: noop,
};

export const CatalogSelectionContext = createContext<CatalogSelection>(defaultCatalogSelection);
export const useCatalogSelectionContext = () => useContext(CatalogSelectionContext);

export const CatalogSelectionContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [isMatchingOnKey, update] = useState<boolean>(false);
  const { catalogQueryDetails } = useCatalogQueryContext();
  const { catalog, catalogFieldDetails } = useCatalogContext();
  const [isEnabled, setIsEnabled] = useState(false);
  const isFeatureFlagged = useCatInSegQueryBuilderV2FeatureFlag();

  const isMatchingOnProperty = option.isSome(catalogFieldDetails);
  const hasCatalog = option.isSome(catalog);
  const searchFieldType = catalogQueryDetails?.fieldType ?? FieldType.UNDEFINED;

  const legacyIsEnabled = hasCatalog && (isMatchingOnKey || isMatchingOnProperty);

  /** useEffect to update enablement for QBForm */
  useEffect(() => {
    setIsEnabled(hasCatalog && (isMatchingOnKey || isMatchingOnProperty));
  }, [hasCatalog, isMatchingOnKey, isMatchingOnProperty]);

  // update isMatchingOnKey if searchFieldType doesn't match String
  useEffect(() => {
    if (catalogQueryDetails && searchFieldType !== FieldType.STRING) {
      update(false);
    }
  }, [searchFieldType]);

  const value = {
    isMatchingOnKey,
    isMatchingOnProperty,
    hasCatalog,
    setIsEnabled,
    update,
    isEnabled: isFeatureFlagged ? isEnabled : legacyIsEnabled,
  };

  return (
    <CatalogSelectionContext.Provider value={value}>{children}</CatalogSelectionContext.Provider>
  );
};
